'use strict';
/* ==========================================================================
   levels/rap_tracks.js — the coasters of the rap video (levels/rap.js), kept
   apart from core-local/tracks.js. Every cart the video draws comes from one of
   these Coaster.Sims (sampled at song time), and tools/qa_rap.js asserts the
   physics in node, like tools/energycheck.js does for core-local/tracks.js.

     const RT = RapTracks.get('ride', { tPark: 13.2 });   // cached per option set
     RT.track, RT.sim (RT.lift for 'ride'), RT.marks

   ride     verse 1 + chorus 1: the chain lift (0 → 20 m, its speed chosen so the cart parks on
            the crest at tPark, the song's "parked up at the top"), then — as a second sim from
            the crest — the frictionless drop and a string of hills (and one loop) with brakes.
            PE ↔ KE swap on every hill; the total stays put.
   friction verse 2's friction bars: hills 20 / 15 / 11 / 8 m with rolling friction (μ = 0.14,
            booked as heat): every climb is lower, the wheels warm up, it settles on the flat.
   loop     the finale: a 20 m drop into a 12 m loop (frictionless) and a brake run,
            replayed every few bars.
   race     verse 2's elephant tie: two identical 20 m drops (steeper and with a shorter brake
            run-out than core 's4', so the carts reach the bottom 3.1 s after release and stop inside
            their half of the screen), 50 kg vs 5000 kg, frictionless drop: they tie to the millisecond.
   ========================================================================== */
(function (root) {
  const isNode = typeof window === 'undefined' && typeof require === 'function';
  const C = isNode ? require('../core-local/coaster.js') : root.Coaster;

  const DEFS = {
    ride: {
      hills: [{ x: 0, h: 0 }, { x: 4, h: 0 }, { x: 26, h: 20 }, { x: 29.5, h: 20 }, { x: 52, h: 0 },
        { x: 72, h: 13 }, { x: 90, h: 1 }, { x: 110, h: 16 }, { x: 132, h: 0 }, { x: 146, h: 0, loop: { r: 6, drift: 5 } },
        { x: 160, h: 0 }, { x: 180, h: 11 }, { x: 198, h: 1 }, { x: 216, h: 14 }, { x: 238, h: 0 }, { x: 330, h: 0 }],
      marks: { liftStart: 1, liftTop: 29, crest: 29.5, bottom: 52, loop: 146 },
    },
    friction: {
      hills: [{ x: 0, h: 20 }, { x: 2.5, h: 20 }, { x: 14, h: 1 }, { x: 24, h: 15 }, { x: 33, h: 1 }, { x: 41, h: 11 },
        { x: 48, h: 1 }, { x: 55, h: 8 }, { x: 62, h: 1 }, { x: 100, h: 1 }],
      marks: { crests: [2.5, 24, 41, 55], heights: [20, 15, 11, 8] },
    },
    race: {
      hills: [{ x: 0, h: 20 }, { x: 2, h: 20 }, { x: 18, h: 0 }, { x: 62, h: 0 }],
      marks: { crest: 2, bottom: 18 },
    },
    loop: {
      hills: [{ x: 0, h: 20 }, { x: 3, h: 20 }, { x: 25, h: 0 }, { x: 38, h: 0, loop: { r: 6, drift: 5 } }, { x: 52, h: 0 }, { x: 110, h: 0 }],
      marks: { crest: 3, bottom: 25, loop: 38 },
    },
  };
  const SIMS = {
    // lift: chain from the station (x = 1) to the far end of the crest plateau, then parked (hold)
    // (x1 = where the ride sim starts, a hair down the lip, so the cart doesn't hop when the drop begins)
    lift: o => ({ track: 'ride', m: 500, mu: 0, x0: 1, v0: 0, chain: { x0: 1, x1: o.x1, speed: o.chainSpeed, hold: true }, tMax: o.tPark + 1 }),
    // the drop, from rest-ish at the lip of the plateau: a 0.3 m/s nudge, paid for by starting a hair down
    // the lip (h0, Sim.release) so the total is exactly 100,000 J; the lift parks right there
    ride: () => ({ track: 'ride', m: 500, mu: 0, x0: 29.5, v0: 0.3, h0: 20, brakes: { x0: 250, x1: 330, mu: 0.7 }, tMax: 30 }),
    friction: () => ({ track: 'friction', m: 500, mu: 0.12, x0: 2.5, v0: 0.5, h0: 20, brakes: { x0: 70, x1: 100, mu: 0.5 }, tMax: 16 }),
    raceMax: () => ({ track: 'race', m: 50, mu: 0, x0: 2.3, v0: 0.5, h0: 20, brakes: { x0: 26, x1: 62, mu: 1.2 }, tMax: 10 }),
    raceElephant: () => ({ track: 'race', m: 5000, mu: 0, x0: 2.3, v0: 0.5, h0: 20, brakes: { x0: 26, x1: 62, mu: 1.2 }, tMax: 10 }),
    loop: () => ({ track: 'loop', m: 500, mu: 0, x0: 3, v0: 0.5, h0: 20, brakes: { x0: 58, x1: 110, mu: 1.0 }, tMax: 12 }),
  };

  const trackCache = {}, cache = {};
  const trackOf = id => trackCache[id] || (trackCache[id] = new C.Track({ hills: DEFS[id].hills }));

  /* get(id, o): id ∈ ride | race | friction | loop.  ride needs o.tPark (s from song start to parking on the crest). */
  function get(id, o = {}) {
    const key = id + '|' + (o.tPark ? o.tPark.toFixed(3) : '');
    if (cache[key]) return cache[key];
    const R = { id, marks: Object.assign({}, DEFS[id].marks) };
    R.track = trackOf(id);
    const mk = (fn, extra) => { const d = fn(extra || {}); return new C.Sim(Object.assign({}, d, { track: trackOf(d.track) })); };
    if (id === 'ride') {
      const tPark = o.tPark || 13;
      R.sim = mk(SIMS.ride);
      const x1 = R.track.x(R.sim.s0), sLift = R.sim.s0 - R.track.sAtX(1);
      R.chainSpeed = sLift / tPark;
      R.lift = mk(SIMS.lift, { chainSpeed: R.chainSpeed, tPark, x1 });
      R.marks.tTop = R.lift.tTop;
      R.marks.tBottom = R.sim.timeAtX(R.marks.bottom);
      R.marks.tLoop = R.sim.timeAtX(R.marks.loop);
    } else if (id === 'race') {
      R.sims = { max: mk(SIMS.raceMax), elephant: mk(SIMS.raceElephant) };
      R.sim = R.sims.max;
      R.marks.tBottomMax = R.sims.max.timeAtX(R.marks.bottom);
      R.marks.tBottomElephant = R.sims.elephant.timeAtX(R.marks.bottom);
    } else {
      R.sim = mk(SIMS[id]);
    }
    return (cache[key] = R);
  }

  const RapTracks = { defs: DEFS, sims: SIMS, get };
  if (typeof module !== 'undefined' && module.exports) module.exports = RapTracks;
  if (typeof window !== 'undefined') window.RapTracks = RapTracks;
})(this);
